/**
 * Single in-process WebSocket manager.
 *
 * One socket is opened per `(scope, id)` pair; multiple subscribers share
 * the same connection through an event bus. We also:
 *
 *   * Re-open the socket whenever the JWT access token changes — old
 *     connections fail closed because the server validates on upgrade.
 *   * Apply exponential back-off (max 30 s) on disconnect, capped by
 *     the page being hidden — there's no point thrashing while in the
 *     background tab.
 *
 * The transport choice (subprotocol "bearer.<token>") matches what the
 * server expects (see `ws.middleware.JWTAuthMiddleware`).
 */

import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/shared/auth/store";
import { env } from "@/shared/config/env";
import type { WsEnvelope } from "./models";

type ChannelKey =
  | `site:${number}:sensors`
  | `site:${number}:risk`
  | `site:${number}:alerts`
  | `site:${number}:evac`
  | `site:${number}:dispersion`
  | `org:${number}:alerts`;

interface Subscription {
  url: string;
  protocols?: string | undefined;
  ws?: WebSocket | undefined;
  refs: number;
  attempts: number;
  retryHandle?: ReturnType<typeof setTimeout> | undefined;
  listeners: Set<(env: WsEnvelope) => void>;
  status: WsStatus;
  statusListeners: Set<(s: WsStatus) => void>;
}

export type WsStatus = "connecting" | "open" | "closed" | "reconnecting";

const subs = new Map<ChannelKey, Subscription>();

function path(key: ChannelKey): string {
  if (key.startsWith("site:")) {
    const [, id, kind] = key.split(":");
    return `/ws/sites/${id}/${kind}`;
  }
  if (key.startsWith("org:")) {
    const [, id] = key.split(":");
    return `/ws/org/${id}/alerts`;
  }
  throw new Error(`Unknown channel key: ${key}`);
}

/**
 * WebSocket 오리진.
 *
 * ⚠️ `window.location` 을 그대로 쓰면 **네이티브 앱에서 실시간 경보가 죽는다**.
 * Capacitor 는 번들된 웹을 `https://localhost` 로 서빙하므로(androidScheme:"https")
 * `window.location.host` 가 `localhost` 가 되어 `wss://localhost/ws/...` 로 붙으려
 * 하고 영원히 실패한다. HTTP 는 `env.apiBase` 절대주소를 쓰는데 WS 만 상대 오리진을
 * 쓰고 있어 앱에서만 조용히 끊기는 형태였다.
 *
 * 그래서 `env.apiBase` 가 있으면 그 오리진의 스킴을 ws/wss 로 바꿔 쓰고, 없을 때
 * (개발 서버 · Vite 프록시)만 현재 위치로 떨어진다.
 */
function origin(): string {
  const base = env.apiBase;
  if (base) {
    try {
      const u = new URL(base, window.location.href);
      return `${u.protocol === "https:" ? "wss:" : "ws:"}//${u.host}`;
    } catch {
      /* 잘못된 VITE_API_BASE → 아래 상대 오리진으로 폴백 */
    }
  }
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}`;
}

function setStatus(sub: Subscription, status: WsStatus) {
  sub.status = status;
  sub.statusListeners.forEach((cb) => cb(status));
}

function open(key: ChannelKey, sub: Subscription, accessToken: string | null) {
  if (sub.ws && sub.ws.readyState <= WebSocket.OPEN) return;

  const url = origin() + path(key);
  // The browser WebSocket constructor accepts a string OR string[] for
  // protocols; we use the "bearer.<token>" convention the server expects.
  const protocols = accessToken ? [`bearer.${accessToken}`] : undefined;
  setStatus(sub, sub.attempts === 0 ? "connecting" : "reconnecting");

  const ws = new WebSocket(url, protocols);
  sub.ws = ws;

  ws.addEventListener("open", () => {
    if (sub.ws !== ws) return;                               // 이미 바뀐 지난 소켓 — 아래 close 와 같은 이유
    sub.attempts = 0;
    setStatus(sub, "open");
  });

  ws.addEventListener("message", (ev) => {
    try {
      const env = JSON.parse(ev.data) as WsEnvelope;
      sub.listeners.forEach((cb) => cb(env));
    } catch {
      /* ignore non-JSON frames */
    }
  });

  ws.addEventListener("close", (ev) => {
    // 토큰 갱신·재구독으로 이미 새 소켓으로 바뀐 지난 소켓의 늦은 close — 새 소켓이 열린 뒤에 오면 상태를
    // "closed" 로 덮고(재시도는 새 소켓이 OPEN 이라 그냥 돌아가 상태가 굳는다) 경보 폴링이 끊김 간격(10초)으로
    // 계속 돈다. 지금 소켓의 이벤트만 상태·재시도에 반영한다(2026-10-19 검증).
    if (sub.ws !== ws) return;
    setStatus(sub, "closed");
    if (sub.refs === 0) return;                              // intentionally closed
    if (ev.code === 4401 || ev.code === 4403) return;        // auth failures: don't loop
    sub.attempts += 1;
    const delay = Math.min(30_000, 1_000 * 2 ** Math.min(sub.attempts, 5));
    sub.retryHandle = setTimeout(() => {
      const t = useAuth.getState().access;
      open(key, sub, t);
    }, delay);
  });

  ws.addEventListener("error", () => {
    /* delegated to onclose */
  });
}

function close(sub: Subscription) {
  if (sub.retryHandle) clearTimeout(sub.retryHandle);
  sub.retryHandle = undefined;
  if (sub.ws && sub.ws.readyState <= WebSocket.OPEN) sub.ws.close(1000, "client");
  sub.ws = undefined;
  sub.attempts = 0;
  setStatus(sub, "closed");
}

function subscribe(
  key: ChannelKey,
  onMessage: (env: WsEnvelope) => void,
  onStatus?: (s: WsStatus) => void,
): () => void {
  let sub = subs.get(key);
  if (!sub) {
    sub = {
      url: origin() + path(key),
      refs: 0,
      attempts: 0,
      listeners: new Set(),
      status: "closed",
      statusListeners: new Set(),
    };
    subs.set(key, sub);
  }
  sub.refs += 1;
  sub.listeners.add(onMessage);
  if (onStatus) {
    sub.statusListeners.add(onStatus);
    // 이미 열려 있는 소켓에 늦게 붙은 구독자에게 **지금 상태를 바로** 알린다. 상태는 바뀔 때만 흘러가서,
    // 앱 셸에 늘 붙어 있는 훈련 표식(TrainingModeWatermark 의 sensors 구독)이 먼저 연 현장 채널에 센서 노드 화면이
    // 붙으면 초기값 「closed」 그대로 「오프라인」 배지가 떴다 — 다른 현장 탭을 갔다 와야(새 소켓) 「실시간」이 됐다
    // (2026-10-29 사용자 「센서노드 화면에서 … 오프라인으로 표시되고, 가람2 탭 갔다 오면 실시간으로」·「처음에는 무조건
    // 뭘로 와도 오프라인」). 경보 채널도 같다 — 끊김으로 알고 폴링을 짧은 간격으로 돌았다.
    if (sub.refs > 1) onStatus(sub.status);
  }
  if (sub.refs === 1) open(key, sub, useAuth.getState().access);
  return () => {
    sub!.refs -= 1;
    sub!.listeners.delete(onMessage);
    if (onStatus) sub!.statusListeners.delete(onStatus);
    if (sub!.refs === 0) close(sub!);
  };
}

// Re-open every active socket when the access token rotates.
useAuth.subscribe(
  (s) => s.access,
  (next) => {
    subs.forEach((sub, key) => {
      if (sub.refs > 0) {
        if (sub.ws) sub.ws.close(1000, "token-rotated");
        open(key, sub, next);
      }
    });
  },
);

/* ─── React hooks ────────────────────────────────────────────────────── */

export function useWsChannel<T = unknown>(
  key: ChannelKey | null,
  onMessage: (env: WsEnvelope<T>) => void,
): WsStatus {
  const [status, setLocalStatus] = useState<WsStatus>("closed");
  const cb = useRef(onMessage);
  cb.current = onMessage;

  useEffect(() => {
    if (!key) return;
    const off = subscribe(
      key,
      (env) => cb.current(env as WsEnvelope<T>),
      setLocalStatus,
    );
    return off;
  }, [key]);

  return status;
}

export function useSensorChannel(
  siteId: number | null,
  onReading: (env: WsEnvelope<import("./models").SensorReadingPayload>) => void,
): WsStatus {
  return useWsChannel(siteId ? `site:${siteId}:sensors` : null, onReading);
}

export function useAlertChannel(
  siteId: number | null,
  onAlert: (env: WsEnvelope<import("./models").AlertFiredPayload>) => void,
): WsStatus {
  return useWsChannel(siteId ? `site:${siteId}:alerts` : null, onAlert);
}

export function useRiskChannel(
  siteId: number | null,
  onRisk: (env: WsEnvelope<import("./models").RiskUpdatedPayload>) => void,
): WsStatus {
  return useWsChannel(siteId ? `site:${siteId}:risk` : null, onRisk);
}

export function useEvacChannel<T = unknown>(
  siteId: number | null,
  onEvent: (env: WsEnvelope<T>) => void,
): WsStatus {
  return useWsChannel(siteId ? `site:${siteId}:evac` : null, onEvent);
}

/**
 * Recalibrated-dispersion channel (`/ws/sites/<id>/dispersion`, P3-A). The
 * 1-min Kalman beat pushes `dispersion.updated` with the reconciled heat-map +
 * per-node correction meta. The 확산 패널 subscribes to show live 보정 상태.
 */
export function useDispersionChannel<T = unknown>(
  siteId: number | null,
  onEvent: (env: WsEnvelope<T>) => void,
): WsStatus {
  return useWsChannel(siteId ? `site:${siteId}:dispersion` : null, onEvent);
}

/**
 * Org-scoped alert channel (`/ws/org/<orgId>/alerts`). Carries `alert.fired`,
 * `alert.cleared`, and the 119 gate cards (`emergency.gate.opened` /
 * `emergency.gate.decided`) for every site in the org — the dashboard-wide
 * feed. Pass the real organization id (not a site id).
 */
export function useOrgAlertChannel<T = unknown>(
  orgId: number | null,
  onEvent: (env: WsEnvelope<T>) => void,
): WsStatus {
  return useWsChannel(orgId ? `org:${orgId}:alerts` : null, onEvent);
}
